import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Lock } from 'lucide-react';
import SearchSelect from '../components/shared/SearchSelect';
import QcReviewerPicks, { planFor, reviewerPickGaps, appendReviewerPicks } from '../components/workflow/QcReviewerPicks';
import { Field, FilePicker, inputCls, inputBaseCls, btnGhost } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { FormShell, FormSection } from '../components/workflow/FormShell';
import RecordBlockers, { recordBlockReasons } from '../components/workflow/RecordBlockers';
import { EqrSelect } from '../components/workflow/QcForm';
import RefLink from '../components/shared/RefLink';
import WorkflowAddendaList from '../components/records/WorkflowAddendaList';
import { addendumClientNames } from '../components/records/AddendumRow';
import { formatDate } from '../utils/dateFormat';
import { fetchJSON } from '../utils/api';
import { recordOptionLabel } from './WorkflowQcaNew';
import {
  NATURE_OPTIONS, IMPACT_OPTIONS, PURPOSE_TEXT, DETAILS_HINT, CONCLUSION_TEXT, RISK_OPTIONS,
  titleFor, emptyAddendumForm, addendumRequiredGaps, addendumRuleProblems, eqrLabel, NO_EQR_LABEL,
  isPerEngagementRisk, engagementLabelOf,
} from './qcAddendum';

// QC6 / QC7 Addendum — submission form (docs/modules/workflow-qcad.md).
// Step 1 picks the APPROVED QC record; step 2 is the firm's addendum template
// with the header rows read off the record (client / engagement type / period /
// QC form reference / date of original approval) and the requester's own
// entries (nature, details, impact). `QcadForm` is shared with the detail
// page's Modify modal and with Management's "Add addendum".

const LockedRow = ({ label, children }) => (
  <Field label={label}>
    <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 max-w-full">
      <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" title="From the QC record" />
      <span className="text-sm font-semibold text-slate-900 break-words">{children || '—'}</span>
    </div>
  </Field>
);

// A record covering several engagements is revised engagement by engagement:
// each row shows the engagement's current rating (locked) and takes an
// optional "to" — only the engagements that move get one, each with its own
// reason (qcAddendum.addendumRiskFieldsOf).
const RiskPerEngagement = ({ byEngagement, form, setForm, disabled }) => {
  const changes = form.riskChanges || {};
  const setChange = (eid, patch) => setForm((prev) => {
    const cur = { ...(prev.riskChanges || {}) };
    const next = { ...(cur[eid] || {}), ...patch };
    if (!next.to) delete cur[eid]; else cur[eid] = next;
    return { ...prev, riskChanges: cur };
  });
  return (
    <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-200">
      {byEngagement.map((b) => {
        const eid = String(b.engagementId);
        const c = changes[eid] || {};
        return (
          <div key={eid} className="px-3 py-2 space-y-1.5">
            <div className="text-sm font-medium text-slate-800 break-words">{engagementLabelOf(b)}</div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-slate-600">From</span>
              <span className="px-2 py-1 rounded border border-slate-200 bg-slate-50 font-semibold text-slate-900">{b.rating || '—'}</span>
              <span className="text-slate-600">to</span>
              <select
                value={c.to || ''} disabled={disabled}
                onChange={(e) => setChange(eid, { to: e.target.value })}
                className={`${inputBaseCls} w-40`}
                aria-label={`Revised risk rating — ${engagementLabelOf(b)}`}
              >
                <option value="">Unchanged</option>
                {RISK_OPTIONS.filter((r) => r !== b.rating).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            {b.revisedBy && (
              <p className="text-xs text-slate-500">Set by {b.revisedBy} (originally {b.original || '—'}).</p>
            )}
            {c.to && (
              <textarea
                rows="2" value={c.reason || ''} disabled={disabled}
                onChange={(e) => setChange(eid, { reason: e.target.value })}
                placeholder="Reason for revising this engagement's rating"
                aria-label={`Reason — ${engagementLabelOf(b)}`}
                className={inputCls}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * The addendum fields. `record` = the parent QC record (with engagements),
 * `risk` = { rating, original, revisedBy, byEngagement? } from the server
 * (the locked "Revised from"). The reviewer picks live OUTSIDE this template
 * (they are a workflow concern — Management's manual entry never shows them).
 */
export const QcadForm = ({ record, risk, eqr = null, form, setForm, anchor = () => null, manual = false }) => {
  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  // Choosing "Revised" opens the EQR row, prefilled with the current
  // reviewer; the requester may keep, change or clear it.
  const chooseRevised = () => setForm((prev) => ({
    ...prev, riskChanged: 'revised',
    eqrTo: prev.riskChanged === 'revised' ? prev.eqrTo : ((eqr && eqr.eqr) || ''),
  }));
  const primary = (record.engagements || [])[0] || {};
  const clientNames = addendumClientNames(record.engagements);
  const toggleNature = (code) => set('nature', (form.nature || []).includes(code)
    ? form.nature.filter((c) => c !== code) : [...(form.nature || []), code]);
  const perEngagement = isPerEngagementRisk(risk && risk.byEngagement);
  return (
    <div className="space-y-4">
      <div className="text-sm font-semibold text-slate-900">{titleFor(record.qcType)}</div>
      <Field label="Firm's Name" required field={anchor('firmName')}>
        <input type="text" value={form.firmName} onChange={(e) => set('firmName', e.target.value)} className={inputCls} />
      </Field>
      <LockedRow label="Client Name">{clientNames.length ? clientNames.join(', ') : record.entityName}</LockedRow>
      <LockedRow label="Engagement Type">{primary.serviceProvided}</LockedRow>
      <LockedRow label="Period End">{formatDate(primary.yearEnd || record.yearEnd)}</LockedRow>
      {/* The form this addendum extends = the RECORD as it stands today (a QC
          Amendment edits it in place), not the application that first
          produced it. Management's manual entry sits on the record already. */}
      <Field label="QC Form Reference">
        <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50">
          <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span className="text-sm font-semibold text-slate-900">{record.qcType}</span>
          {record.legacyImport && <span className="text-xs text-slate-500">{record.signedFormPending ? 'signed form pending' : 'signed form uploaded'}</span>}
          {!manual && record.id && (
            <RefLink
              to={`/workflow/qcad/record/${record.id}`}
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
              title="Open the current QC record in a new tab"
            >
              View Current Record <ExternalLink className="w-3.5 h-3.5" />
            </RefLink>
          )}
        </div>
      </Field>
      <LockedRow label="Date of Original Approval">{record.approvedOn ? formatDate(record.approvedOn) : '—'}</LockedRow>

      <Field label="Purpose of Addendum">
        <p className="text-sm text-slate-600 leading-relaxed">{PURPOSE_TEXT}</p>
      </Field>

      <Field label="Nature of Update / Change" required field={anchor('nature')}>
        <div className="space-y-1.5">
          {NATURE_OPTIONS.map(([code, label]) => (
            <label key={code} className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={(form.nature || []).includes(code)} onChange={() => toggleNature(code)} />
              {label}
            </label>
          ))}
          {(form.nature || []).includes('other') && (
            <input type="text" value={form.natureOther} onChange={(e) => set('natureOther', e.target.value)} className={`${inputCls} mt-1`} placeholder="Please specify" />
          )}
        </div>
      </Field>

      <Field label="Details of Update" required hint={DETAILS_HINT} field={anchor('details')}>
        <textarea rows="5" value={form.details} onChange={(e) => set('details', e.target.value)} className={inputCls} />
      </Field>

      <Field label="Impact Assessment — Risk Rating" required field={anchor('riskChanged')}>
        {/* Two stacked choices; the one not chosen fades out, so "Unchanged"
            visibly retires the Revised from / to controls instead of leaving
            a live-looking dropdown beside it. */}
        <div className="space-y-2">
          <label className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${form.riskChanged === 'unchanged' ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
            <input type="radio" name="riskChanged" checked={form.riskChanged === 'unchanged'}
              onChange={() => setForm((prev) => ({ ...prev, riskChanged: 'unchanged', riskTo: '', riskChanges: {}, eqrTo: '' }))} />
            <span className="text-sm text-slate-800">
              <span className="font-medium">Unchanged</span>
              <span className="text-slate-500"> — stays {risk && risk.rating ? <span className="font-semibold text-slate-800">{risk.rating}</span> : '—'}</span>
            </span>
          </label>
          {perEngagement ? (
            <div className={`px-3 py-2.5 rounded-lg border transition-colors space-y-2 ${form.riskChanged === 'revised' ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-white'} ${form.riskChanged === 'unchanged' ? 'opacity-50' : ''}`}>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="radio" name="riskChanged" checked={form.riskChanged === 'revised'} onChange={chooseRevised} />
                <span className="text-sm font-medium text-slate-800">Revised — per engagement</span>
              </label>
              {form.riskChanged === 'revised' && (
                <RiskPerEngagement byEngagement={risk.byEngagement} form={form} setForm={setForm} disabled={false} />
              )}
            </div>
          ) : (
          <label className={`flex flex-wrap items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${form.riskChanged === 'revised' ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-white hover:bg-slate-50'} ${form.riskChanged === 'unchanged' ? 'opacity-50' : ''}`}>
            <input type="radio" name="riskChanged" checked={form.riskChanged === 'revised'} onChange={chooseRevised} />
            <span className="text-sm font-medium text-slate-800">Revised from</span>
            <span className="px-2 py-1 rounded border border-slate-200 bg-white text-sm font-semibold text-slate-900">
              {risk && risk.rating ? risk.rating : '—'}
            </span>
            <span className="text-sm text-slate-600">to</span>
            {form.riskChanged === 'revised' ? (
              <select value={form.riskTo} onChange={(e) => set('riskTo', e.target.value)} onClick={(e) => e.stopPropagation()} className={`${inputBaseCls} w-40`}>
                <option value="">Select…</option>
                {RISK_OPTIONS.filter((r) => !risk || r !== risk.rating).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            ) : (
              <span className="inline-flex items-center px-3 py-2 w-40 rounded-lg border border-dashed border-slate-300 text-sm text-slate-400">—</span>
            )}
          </label>
          )}
        </div>
        {risk && risk.revisedBy && !perEngagement && (
          <p className="text-xs text-slate-500 mt-1">Current rating {risk.rating} was set by {risk.revisedBy} (originally {risk.original || '—'}).</p>
        )}
      </Field>

      {/* A revised rating may change who reviews the engagement. Optional —
          the rating does not make it mandatory; prefilled with the current
          reviewer, who may be kept, replaced or cleared. Not asked at all
          while the rating is unchanged. */}
      {form.riskChanged === 'revised' && (
        <Field
          label="Engagement Quality Reviewer After This Addendum"
          field={anchor('eqrTo')}
          hint="Optional. Prefilled with the current reviewer — keep, change or clear it. On approval the covered engagements take this reviewer."
        >
          <div className="space-y-2">
            <div className="text-xs text-slate-600">
              Current: <span className="font-semibold text-slate-800">{eqrLabel(eqr && eqr.eqr)}</span>
              {eqr && eqr.revisedBy ? ` (set by ${eqr.revisedBy}; the form named ${eqrLabel(eqr.original)})` : ''}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="min-w-[16rem] flex-1">
                <EqrSelect value={form.eqrTo || ''} onChange={(v) => set('eqrTo', v)} />
              </div>
              {form.eqrTo && (
                <button type="button" onClick={() => set('eqrTo', '')} className={btnGhost}>Clear</button>
              )}
            </div>
            {!form.eqrTo && (eqr && eqr.eqr) && (
              <p className="text-xs text-amber-700">Cleared — on approval the covered engagements will have no engagement quality reviewer ({NO_EQR_LABEL}).</p>
            )}
            {form.eqrTo && (eqr && eqr.eqr) !== form.eqrTo && (
              <p className="text-xs text-slate-600">Changes the reviewer from {eqrLabel(eqr && eqr.eqr)} to <span className="font-medium text-slate-800">{form.eqrTo}</span>.</p>
            )}
          </div>
        </Field>
      )}

      <Field label="Impact Assessment — Independence / Ethical Considerations" required field={anchor('independenceImpact')}>
        <div className="space-y-1.5">
          {IMPACT_OPTIONS.map(([code, label]) => (
            <label key={code} className="flex items-center gap-2 text-sm text-slate-700">
              <input type="radio" name="independenceImpact" checked={form.independenceImpact === code} onChange={() => set('independenceImpact', code)} />
              {label}
            </label>
          ))}
        </div>
      </Field>

      <Field label="Conclusion">
        <p className="text-sm text-slate-600">{CONCLUSION_TEXT}</p>
      </Field>
    </div>
  );
};

/** FormData for POST / PATCH — one builder for every surface. */
export const buildQcadBody = (form, { files = [], record } = {}) => {
  const fd = new FormData();
  fd.set('firmName', form.firmName || '');
  fd.set('nature', JSON.stringify(form.nature || []));
  fd.set('natureOther', form.natureOther || '');
  fd.set('details', form.details || '');
  fd.set('riskChanged', form.riskChanged || '');
  fd.set('riskTo', form.riskTo || '');
  // Per engagement (a record covering several): only the engagements that move.
  fd.set('riskChanges', JSON.stringify(form.riskChanged === 'revised' ? (form.riskChanges || {}) : {}));
  // Sent only with a revised rating: present-and-blank means "clear the
  // reviewer", absent means "leave it" (the server's addendumEqrFieldsOf).
  if (form.riskChanged === 'revised') fd.set('eqrTo', form.eqrTo || '');
  fd.set('independenceImpact', form.independenceImpact || '');
  appendReviewerPicks(fd, qcadReviewerPlan(record, form), form);
  files.forEach((f) => fd.append('files', f));
  return fd;
};

/**
 * The reviewer plan of an addendum: the parent record's form answers decide
 * the route (engagement type, non-assurance service type — lenient for a
 * record that never answered them), the addendum's own "rating revised"
 * answer is the QC7 risk change.
 */
export const qcadReviewerPlan = (record, form) => planFor(record.answers || {}, record.qcType, {
  lenient: true,
  riskChanged: record.qcType === 'QC7' && form.riskChanged === 'revised',
});

/**
 * The reviewer picks of an addendum — outside the template so Management's
 * manual entry never renders them. The plan follows the parent record's
 * answers; a revised QC7 rating adds a Partner stage.
 */
export const QcadReviewerFields = ({ record, form, setForm }) => {
  const plan = useMemo(() => qcadReviewerPlan(record, form), [record, form]);
  const picks = useMemo(() => ({
    reviewerStaffId: form.reviewerStaffId || '',
    additionalReviewerStaffId: form.additionalReviewerStaffId || '',
    thirdReviewerStaffId: form.thirdReviewerStaffId || '',
  }), [form.reviewerStaffId, form.additionalReviewerStaffId, form.thirdReviewerStaffId]);
  const onChange = useCallback((next) => setForm((prev) => ({ ...prev, ...next })), [setForm]);
  return (
    <QcReviewerPicks
      plan={plan}
      picks={picks}
      onChange={onChange}
      qcType={record.qcType}
      hint="You pick who reviews this addendum, from the firm's roster."
    />
  );
};

/** The reviewer gaps of an addendum form (New page + Modify modal). */
export const qcadReviewerGaps = (record, form) => reviewerPickGaps(qcadReviewerPlan(record, form), form);

/** Load a record's addendum context (record, current risk, addenda, QMP exclusions). */
export const useQcadRecord = (recordId) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!recordId) { setData(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const d = await fetchJSON(`/api/workflow/qcad/record/${recordId}`);
        if (!cancelled) setData(d);
      } catch (e) { if (!cancelled) setError(e.message); }
    })();
    return () => { cancelled = true; };
  }, [recordId]);
  return { recordData: data, recordError: error };
};

const WorkflowQcadNew = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [recordId, setRecordId] = useState('');
  const [form, setForm] = useState(emptyAddendumForm());
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const { recordData, recordError } = useQcadRecord(recordId);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchJSON('/api/workflow/qcad/options');
        setRecords(data.records || []);
      } catch (e) { setError(e.message); }
    })();
  }, []);

  const record = recordData?.record || null;
  const risk = recordData?.risk || null;
  const eqr = recordData?.eqr || null;
  // Why this record cannot take an addendum right now (closed engagement, an
  // amendment / addendum in flight) — said under the picker, Submit disabled.
  const blockers = record && recordData
    ? recordBlockReasons({ engagementLock: recordData.engagementLock, openRequests: recordData.openRequests, flow: 'qcad' })
    : [];

  // Everything still empty, keyed to the control it belongs to — after the
  // first Submit the page marks those controls and lists them in the banner.
  // No record yet ⇒ the record picker is the only gap (the addendum is not on
  // screen); with one, the shared addendum check names its empty controls and
  // the reviewer picks add theirs.
  const gaps = useMemo(() => (record
    ? [...addendumRequiredGaps(form, { needsQmp: false, byEngagement: risk && risk.byEngagement }), ...qcadReviewerGaps(record, form)]
    : [{ key: 'qcRecord', label: 'QC6 / QC7 Record' }]
  ), [record, form, risk]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    setError(null);
    // Rules on filled-in values (the revised rating must move) stay a banner.
    const problems = addendumRuleProblems(form, { riskFrom: risk?.rating || null, byEngagement: risk?.byEngagement });
    if (problems.length > 0) { setError(problems.join('. ')); return; }
    setSaving(true);
    try {
      const fd = buildQcadBody(form, { files, record });
      fd.set('qcRecordId', String(record.id));
      const data = await fetchJSON('/api/workflow/qcad', { method: 'POST', body: fd });
      navigate(`/workflow/qcad/${data.request.id}`);
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormShell
      backTo="/workflow/qcad"
      backLabel="QC6 / QC7 Addendum"
      title="New QC6 / QC7 Addendum Request"
      subtitle="Document information identified after a QC6 / QC7 was approved."
      error={error || recordError || validation.error}
      onSubmit={submit}
      submitting={saving}
      submitDisabled={blockers.length > 0}
    >
      <FormValidationProvider value={validation.providerValue}>
        <FormSection step={1} title="Select the QC Record">
          <Field label="QC6 / QC7 Record" required field="qcRecord">
            <SearchSelect
              value={recordId}
              onChange={(v) => { setRecordId(v); setForm(emptyAddendumForm()); }}
              options={records.map((r) => ({ value: r.id, label: recordOptionLabel(r) }))}
              placeholder="Search a QC record…"
            />
            {record && recordData && (
              <RecordBlockers engagementLock={recordData.engagementLock} openRequests={recordData.openRequests} flow="qcad" />
            )}
          </Field>
          {/* What has already been added to the form: the requester writes
              the next addendum knowing the revisions before it — the record
              alone may no longer be the latest word. */}
          {record && (
            <div className="pt-1">
              <p className="text-sm font-semibold text-slate-800 mb-1.5">
                Addenda already filed on this record ({(recordData.addenda || []).length})
              </p>
              {(recordData.addenda || []).length > 0 && (
                <p className="text-xs text-slate-500 mb-2">This addendum will be filed after them. Read them before writing — the latest one is marked.</p>
              )}
              <WorkflowAddendaList record={record} addenda={recordData.addenda || []} />
            </div>
          )}
        </FormSection>

        <FormSection step={2} title="Addendum">
          {record ? (
            <QcadForm record={record} risk={risk} eqr={eqr} form={form} setForm={setForm} anchor={(k) => k} />
          ) : (
            <p className="text-sm text-slate-500">Select a QC record above to fill in the addendum.</p>
          )}
        </FormSection>

        <FormSection step={3} title="Attachments">
          <Field label="Supporting Files" hint="Evidence for the details above. PDF / DOC / DOCX / XLS / XLSX / JPG / PNG.">
            <FilePicker multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" onChange={(e) => setFiles(Array.from(e.target.files || []))} files={files} />
          </Field>
        </FormSection>

        {/* Reviewer picks — only on screen once a record is chosen (the parent
            type decides whether a second pick applies). */}
        {record && (
          <FormSection step={4} title={qcadReviewerPlan(record, form).stages?.length > 1 ? 'Reviewers' : 'Reviewer'}>
            <QcadReviewerFields record={record} form={form} setForm={setForm} />
          </FormSection>
        )}
      </FormValidationProvider>
    </FormShell>
  );
};

export default WorkflowQcadNew;
